import { useCallback, useEffect, useRef } from 'react';
import { UpdateDocumentTaxonomyConfigInput } from 'app/api/tag-queries/types';

type ConfigFields = Omit<UpdateDocumentTaxonomyConfigInput, 'configId'>;

interface UseBatchedConfigUpdateProps {
  configId: string | undefined;
  updateConfig: (input: UpdateDocumentTaxonomyConfigInput) => void;
  delay?: number;
}

export const useBatchedConfigUpdate = ({ configId, updateConfig, delay = 1000 }: UseBatchedConfigUpdateProps) => {
  const pendingRef = useRef<ConfigFields>({});
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const configIdRef = useRef(configId);
  configIdRef.current = configId;

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (!configIdRef.current || Object.keys(pendingRef.current).length === 0) return;
    updateConfig({ configId: configIdRef.current, ...pendingRef.current });
    pendingRef.current = {};
  }, [updateConfig]);

  const update = useCallback(
    (partial: ConfigFields) => {
      Object.assign(pendingRef.current, partial);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, delay);
    },
    [flush, delay]
  );

  // Flush on unmount
  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      flush();
    };
  }, [flush]);

  return { update, flush };
};
